/**
 * ProtectedRoute - Unified permission-based route protection
 *
 * Supports both MSAL (Azure AD B2C) and Kinde authentication providers during
 * the migration period. Checks permissions based on the active provider and
 * redirects unauthorized users to the fallback path.
 *
 * For MSAL users: Uses AuthorisationUtils with idTokenClaims from account
 * For Kinde users: Uses async getPermission() from Kinde SDK
 *
 * Note: Claims fetching for MSAL is handled by MsalAuthBridge in MsalProviderWrapper.
 * The user object from useAuthContext() already includes idTokenClaims.
 *
 * @module ProtectedRoute
 * @see {@link useAuth} for unified auth hook
 * @see {@link AuthorisationUtils} for MSAL permission checking
 */

import { useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import PropTypes from 'prop-types'

import { useAuthContext } from '@/common/components/AuthProvider/AuthContext'
import TbxLoadingOverlay from '@/common/components/TbxLoadingOverlay/TbxLoadingOverlay'
import { AUTH_PROVIDERS, useAuth } from '@/common/hooks/useAuth'
import * as AuthorisationUtils from '@/common/utils/Authorisation/AuthorisationUtils'

/**
 * Permission key mapping for MSAL provider
 *
 * Maps Kinde permission keys to AuthorisationUtils check functions.
 * Note: 'support:site' uses isSiteAdminOrSupport because SiteSupport
 * role grants access when checking for support permissions.
 */
const MSAL_PERMISSION_CHECKS = {
    'admin:site': (account) => AuthorisationUtils.isSiteAdmin(account),
    'support:site': (account) => AuthorisationUtils.isSiteAdminOrSupport(account),
}

/**
 * Check if user has any of the required permissions (MSAL provider)
 *
 * @param {Object} account - MSAL account with idTokenClaims
 * @param {string[]} permissions - Required permissions
 * @returns {boolean} True if user has any of the permissions
 */
const checkMsalPermissions = (account, permissions) => {
    if (!account) return false

    return permissions.some((permission) => {
        const checkFn = MSAL_PERMISSION_CHECKS[permission]
        return checkFn ? checkFn(account) : false
    })
}

/**
 * Check if user has any of the required permissions (Kinde provider)
 *
 * @param {Function} getPermission - Kinde async getPermission function
 * @param {string[]} permissions - Required permissions
 * @returns {Promise<boolean>} True if user has any of the permissions
 */
const checkKindePermissions = async (getPermission, permissions) => {
    const results = await Promise.all(permissions.map((permission) => getPermission(permission)))
    return results.some((result) => result?.isGranted === true)
}

/**
 * ProtectedRoute - Route-level authorization component
 *
 * Wraps routes that require specific permissions. Automatically detects
 * the active auth provider and checks permissions accordingly.
 *
 * @param {Object} props
 * @param {Object} props.has - Permission requirements
 * @param {string[]} props.has.permissions - Required permissions (OR logic - any one grants access)
 * @param {string} [props.fallbackPath='/'] - Redirect path for unauthorized users
 * @param {React.ReactNode} props.children - Protected content to render if authorized
 * @returns {JSX.Element} Children if authorized, Navigate if not, loading overlay while checking
 *
 * @example
 * // Require admin OR support permission
 * <ProtectedRoute has={{ permissions: ['admin:site', 'support:site'] }} fallbackPath="/">
 *   <AdminPage />
 * </ProtectedRoute>
 *
 * @example
 * // Require only admin permission
 * <ProtectedRoute has={{ permissions: ['admin:site'] }} fallbackPath="/administration">
 *   <AdminOnlyPage />
 * </ProtectedRoute>
 */
export const ProtectedRoute = ({ children, fallbackPath = '/', has }) => {
    const { getPermission, isAuthenticated, isLoading, provider } = useAuth()
    const { user } = useAuthContext()

    // State for Kinde async permission check
    const [kindePermissionState, setKindePermissionState] = useState({
        hasPermission: false,
        isChecking: true,
    })

    // Fetch Kinde permissions asynchronously
    useEffect(() => {
        if (provider !== AUTH_PROVIDERS.KINDE || isLoading || !isAuthenticated) {
            return
        }

        let isMounted = true
        const permissions = has?.permissions || []

        const fetchPermissions = async () => {
            try {
                const result = await checkKindePermissions(getPermission, permissions)
                if (isMounted) {
                    setKindePermissionState({
                        hasPermission: result,
                        isChecking: false,
                    })
                }
            } catch (error) {
                console.error('ProtectedRoute: Error checking Kinde permissions', error)
                if (isMounted) {
                    setKindePermissionState({
                        hasPermission: false,
                        isChecking: false,
                    })
                }
            }
        }

        fetchPermissions()

        return () => {
            isMounted = false
        }
    }, [provider, isLoading, isAuthenticated, getPermission, has?.permissions])

    // Check permissions based on provider (MSAL is synchronous)
    const msalPermission = useMemo(() => {
        if (provider !== AUTH_PROVIDERS.MSAL || isLoading || !isAuthenticated) {
            return false
        }

        const permissions = has?.permissions || []
        return checkMsalPermissions(user, permissions)
    }, [provider, isLoading, isAuthenticated, user, has?.permissions])

    // Determine if still loading permissions
    const isCheckingPermissions = useMemo(() => {
        if (isLoading) return true
        if (provider === AUTH_PROVIDERS.KINDE && isAuthenticated) {
            return kindePermissionState.isChecking
        }
        return false
    }, [isLoading, provider, isAuthenticated, kindePermissionState.isChecking])

    // Determine final permission state
    const hasPermission = useMemo(() => {
        if (provider === AUTH_PROVIDERS.MSAL) {
            return msalPermission
        }
        return kindePermissionState.hasPermission
    }, [provider, msalPermission, kindePermissionState.hasPermission])

    // Show loading while auth state or permissions are being determined
    if (isLoading || isCheckingPermissions) {
        return <TbxLoadingOverlay open />
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
        return (
            <Navigate
                to={fallbackPath}
                replace
            />
        )
    }

    // Redirect if no permission
    if (!hasPermission) {
        return (
            <Navigate
                to={fallbackPath}
                replace
            />
        )
    }

    // Authorized - render children
    return children
}

ProtectedRoute.propTypes = {
    /** Protected content to render if authorized */
    children: PropTypes.node.isRequired,
    /** Permission requirements object */
    has: PropTypes.shape({
        /** Array of permission keys (OR logic - any one grants access) */
        permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    /** Redirect path for unauthorized users */
    fallbackPath: PropTypes.string,
}
